import { ValidLocale } from '@localisation'
import { network } from '@utils'
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query'
import { INotification } from './notifications'
import { handleReturn, MutationFunction, QueryFunction } from './root'
import { useHistory } from 'react-router'
import { INocfoPermissions } from './people'
import { TOKEN_STORAGE_KEY } from './constants'
import { usePostHog } from '@posthog/react'
export interface IBusinessUsership {
  business: number
  business_id: string
  permissions: INocfoPermissions
}

export type SubscriptionTier = 'FREE' | 'PAID' | 'NONE'

export type AgencyRole = 'admin' | 'member'

export interface ICurrentUser {
  id: number
  email: string
  first_name: string
  last_name: string
  avatar_url: string
  invitations: []
  is_active: boolean
  last_login: string
  date_joined: string
  notifications: INotification[]
  tos_accepted: boolean
  tos_accepted_by_market: {
    fi: boolean
    de: boolean
  }
  last_csat: string
  userships: IBusinessUsership[]
  show_get_started_info: boolean
  language: ValidLocale
  last_active_business_slug?: string
  subscription_tier: SubscriptionTier
  has_tos_acceptance_record: boolean
  saltedge_tos_accepted_at: string | null
  agency_id: number | null
  is_agency_user: boolean
  agency_role: AgencyRole | null
  is_agency_admin: boolean
  agency_name: string | null
  credit_cap: number | null
  ui_preferences: IUserUIPreferences
}

export interface IUserUIPreferences {
  businesses_table?: {
    columns?: string[]
  }
}

export const useCurrentUser = (
  rqProps?: Partial<UseQueryOptions<any, any, any, any>>
): UseQueryResult<ICurrentUser> =>
  useQuery({
    queryKey: ['user'],
    queryFn: () => fetchCurrentUser(),
    refetchOnMount: false,
    retry: (retryCount: number, error: any) => {
      // Do not retry if the user is not authenticated (401)
      if (error?.status === 401) return false
      return retryCount < 3
    },
    ...(rqProps || {})
  })

export const useIsNocfoStaff = (): boolean => {
  const { data: user } = useCurrentUser()
  return user?.email?.endsWith('@nocfo.io')
}

export const fetchCurrentUser: QueryFunction<void, ICurrentUser> = async () => {
  const url = `/v1/user/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'GET',
    validateStatus: () => true
  })
  return handleReturn({ data, status })
}

export const updateCurrentUser: MutationFunction<void, Partial<ICurrentUser>> = async (
  _,
  requestData
) => {
  const url = `/v1/user/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ data, status })
}

export const logOut: MutationFunction<void, void> = async () => {
  const url = '/auth/logout/'
  await network.httpClient.request({
    url,
    method: 'POST'
  })
}

interface UseLogOutProps {
  redirectTo?: string
}

export const useLogOut = ({ redirectTo = '/' }: UseLogOutProps = {}) => {
  const queryClient = useQueryClient()
  const history = useHistory()
  const posthog = usePostHog()

  const { mutate } = useMutation({
    mutationFn: () => logOut(),
    ...{
      onSettled: async () => {
        posthog.reset()
        network.setBearerToken(undefined)
        sessionStorage.removeItem(TOKEN_STORAGE_KEY)
        queryClient.clear()
        history.push(redirectTo)
        window.location.reload()
      }
    }
  })

  return mutate
}

interface InvitationInstanceProps {
  invitationId: number
}

export const acceptInvitation: MutationFunction<InvitationInstanceProps, void> = async ({
  invitationId
}) => {
  const url = `/v1/user/invitation/${invitationId}/`
  await network.httpClient.request({
    url,
    method: 'PATCH',
    data: { status: 'ACCEPTED' }
  })
}

export const rejectInvitation: MutationFunction<InvitationInstanceProps, void> = async ({
  invitationId
}) => {
  const url = `/v1/user/invitation/${invitationId}/`
  await network.httpClient.request({
    url,
    method: 'PATCH',
    data: { status: 'REJECTED' }
  })
}

export const acceptTos: MutationFunction<{ history_id: number }, void> = async ({ history_id }) => {
  const url = '/v1/user/accept_tos/'
  await network.httpClient.request({
    url,
    method: 'POST',
    data: { history_id }
  })
}

export const acceptSaltedgeTos: MutationFunction<void, void> = async () => {
  const url = '/v1/user/accept_saltedge_tos/'
  await network.httpClient.request({
    url,
    method: 'POST'
  })
}
